import { useMemo } from 'react';

import { Text, VStack } from '@chakra-ui/react';

import { formatHelpers } from '@nocowanie/core';
import { Badge, Tooltip } from '@nocowanie/common-ui';

import { apiTypes } from '@app/models';

import { PriceProps } from './price.props';

export const Price = ({
    currency,
    price,
    basePrice,
    children,
    discounts,
    convertedCurrency,
    convertedPrice,
    convertedBasePrice,
    ...rest
}: PriceProps) => {
    const discount = useMemo(() => {
        if (!basePrice || !price || basePrice === price) {
            return undefined;
        }
        return Math.round(((basePrice - price) / basePrice) * 100).toFixed(0);
    }, [basePrice, price]);

    const renderDiscountInfo = discounts?.map((discount: apiTypes.Discount) => (
        <Text key={discount?.id}>{discount?.discountInfo}</Text>
    ));

    const withForeignPrice = convertedCurrency && convertedCurrency !== currency;

    const renderBadge = discount ? (
        <Badge
            colorPalette={'brand'}
            variant={'solid'}
            px={2}
            py={1}
            rounded={'md'}
            labelProps={{
                fontSize: {
                    base: 'sm',
                    md: 'lg',
                },
            }}
            mb={1}
        >
            {`-${discount}%`}
        </Badge>
    ) : null;

    return (
        <VStack alignItems={'flex-end'} gap={0} {...rest}>
            {discount ? (
                <>
                    <Tooltip
                        positioning={{
                            placement: 'top',
                        }}
                        showTriggerIcon={false}
                        extendedTriggerArea={{
                            additionalTrigger: renderBadge,
                            wrapperProps: {
                                cursor: 'pointer',
                            },
                        }}
                    >
                        {renderDiscountInfo}
                    </Tooltip>
                    <Text as="del" fontSize="xs">
                        {basePrice && formatHelpers.price(basePrice, currency)}
                        {convertedBasePrice && withForeignPrice
                            ? ` (∼${formatHelpers.price(convertedBasePrice, convertedCurrency)})`
                            : null}
                    </Text>
                </>
            ) : null}
            <Text
                as={'strong'}
                fontSize={{ base: 'lg', md: '2xl' }}
                lineHeight={'1'}
                color={discount ? 'brand.solid' : undefined}
            >
                {formatHelpers.price(price, currency)}
                {convertedPrice && withForeignPrice ? (
                    <Text
                        as={'span'}
                        fontWeight={'normal'}
                        fontSize={{ base: 'sm', md: 'lg' }}
                        color={'gray.900'}
                        display={'inline-block'}
                        verticalAlign={'middle'}
                        mb={1}
                        ml={2}
                    >
                        (∼{formatHelpers.price(convertedPrice, convertedCurrency)})
                    </Text>
                ) : null}
            </Text>
            {children}
        </VStack>
    );
};
